<template>
  <v-flex>
    <v-card>
      <v-toolbar
        color="secondary"
        dark
      >
        <v-toolbar-side-icon><v-icon>playlist_play</v-icon></v-toolbar-side-icon>
        <v-toolbar-title>{{$tc('app.playlist', 2)}}</v-toolbar-title>
        <v-spacer></v-spacer>
        <div
          v-if="$vuetify.breakpoint.smAndDown"
          slot="extension"
          class="white--text w-100"
        >
          <v-text-field
            type="text"
            v-model="filterText"
            @keyup.enter="doFilter"
            :placeholder="$t('library.playlistsFilterPlaceholder')"
            single-line
            hide-details
          ></v-text-field>
        </div>
        <v-text-field
          v-if="$vuetify.breakpoint.mdAndUp"
          type="text"
          v-model="filterText"
          @keyup.enter="doFilter"
          :placeholder="$t('library.playlistsFilterPlaceholder')"
          single-line
          hide-details
        ></v-text-field>
        <v-btn
          @click.prevent="doFilter"
          icon
        >
          <v-icon>search</v-icon>
        </v-btn>

        <v-btn
          @click.prevent="resetFilter"
          icon
        >
          <v-icon>restore</v-icon>
        </v-btn>
        <transition
          enter-active-class="animated bounceIn"
          leave-active-class="animated bounceOut"
        >
          <v-btn
            v-if="selectedRows !== undefined && selectedRows.length !== 0"
            @click.prevent="massDeleteModalVisible = true"
            icon
          >
            <v-icon>delete</v-icon>
          </v-btn>
        </transition>
      </v-toolbar>
      <v-card-text>
        <v-progress-linear
          v-show="loading"
          slot="progress"
          color="accent"
          indeterminate
        ></v-progress-linear>

        <vuetable
          ref="vuetable"
          data-path="data"
          pagination-path="meta"
          track-by="id"
          :api-url="`${this.apiUrl}api/playlists`"
          :http-fetch="httpFetch"
          :fields="fields"
          :css="css.table"
          :sort-order="sortOrder"
          :multi-sort="true"
          :append-params="moreParams"
          @vuetable:pagination-data="onPaginationData"
          @vuetable:loading="onLoading"
          @vuetable:loaded="onLoaded"
        >
          <div
            v-if="$store.getters.hasPermission('edit_organization_playlists')"
            slot="actions-slot"
            slot-scope="props"
          >
            <v-btn
              @click="setActivePlaylist(props.rowData)"
              flat
              icon
            >
              <v-icon>edit</v-icon>
            </v-btn>
            <v-btn
              @click="showAssignPlaylistDialog(props.rowData)"
              flat
              icon
            >
              <v-badge color="green" :overlap="true">
                <v-icon>playlist_add</v-icon>
                <template v-if="parseInt(props.rowData.boxes_assigned) > 0"  v-slot:badge>
                  <small>
                    {{ (props.rowData.boxes_assigned > 99) ? "99+" : props.rowData.boxes_assigned }}
                  </small>
                </template>
              </v-badge>
            </v-btn>

            <v-btn
              :disabled="parseInt(props.rowData.boxes_assigned) != 0"
              @click="showDeletePlaylistDialog(props.rowData)"
              flat
              icon
            >
              <v-icon>delete</v-icon>
            </v-btn>
          </div>
        </vuetable>
      </v-card-text>
      <v-card-actions>
        <vuetable-pagination ref="pagination"
          :css="css.pagination"
          @vuetable-pagination:change-page="onChangePage"
        ></vuetable-pagination>
      </v-card-actions>
    </v-card>

    <v-dialog
      v-if="$store.getters.hasPermission('edit_organization_playlists')"
      v-model="createPlaylistModalVisible"
      max-width="500px"
    >
      <v-card>
        <v-card-title primary-title>
          <h3 class="headline">{{ $tc("library.createPlaylist", 1) }}</h3>
        </v-card-title>
        <v-card-text>
          <v-container fluid grid-list-xl>
            <v-layout row wrap>
              <v-flex xs12>
                <v-text-field
                  v-model="newPlaylist.name"
                  v-validate="{ required: true }"
                  data-vv-name="name"
                  name="name"
                  prepend-icon="font_download"
                  type="text"
                  :label="$t('general.name')"
                  :error="errors.has('name')"
                  :error-messages="errors.collect('name')"
                  single-line
                  required
                ></v-text-field>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            @click="createPlaylistModalVisible = false"
            flat
          >{{ $t("general.close") }}</v-btn>
          <v-btn
            color="primary"
            @click.prevent="createPlaylist()"
          >{{ $t("general.create") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-if="$store.getters.hasPermission('edit_organization_playlists')"
      v-model="deletePlaylistModalVisible"
      max-width="500px"
    >
      <v-card>
        <v-card-title primary-title>
          <h3 class="headline">{{ $tc("library.deletePlaylist", 1) }}</h3>
        </v-card-title>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            @click="deletePlaylistModalVisible = false"
            flat
          >{{ $t("general.close") }}</v-btn>
          <v-btn
            @click.prevent="deletePlaylist()"
            color="primary"
          >{{ $t("general.delete") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-if="$store.getters.hasPermission('edit_organization_playlists')"
      v-model="massDeletePlaylistsModalVisible"
      max-width="500px"
    >
      <v-card>
        <v-card-title primary-title>
          <h3 class="headline">{{ $tc("library.deletePlaylist", 2) }}</h3>
        </v-card-title>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            @click="massDeletePlaylistsModalVisible = false"
            flat
          >{{ $t("general.close") }}</v-btn>
          <v-btn
            @click.prevent="massDeletePlaylists()"
            flat
          >{{ $t("general.delete") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-if="$store.getters.hasPermission('edit_organization_playlists')"
      v-model="assignPlaylistModalVisible"
      max-width="500px"
      scrollable
    >
      <v-card>
        <v-card-title primary-title>
          <h3 class="headline">{{ $tc("library.assignPlaylist", 1) }}</h3>

        </v-card-title>
        <v-card-text>

          <v-list>
            <v-list-tile>
              <v-list-tile-action>
                <v-checkbox
                  @change="toggleAll($event)"
                  :input-value="allBoxesSelected"
                  :indeterminate="someBoxesSelected"
                ></v-checkbox>
              </v-list-tile-action>
              <v-select class="form-control" :label="$t('group.select')" :items="groups"
              item-text="name" item-value="boxes"
              @input="onChange()" name="template" v-model="groupSelected" v-if="groups.length">
              </v-select>
            </v-list-tile>

            <v-divider v-if="!groups.length"/>
            <v-list-tile
              v-for="box in boxes"
              :key="box.id"
            >

              <v-list-tile-action>
                <v-checkbox
                  v-model="boxesToAssign"
                  :value="box.id"
                  @change="resetGroup()"
                ></v-checkbox>
              </v-list-tile-action>
              <v-list-tile-content>
                <v-list-tile-title>{{box.name}} ({{box.note}})</v-list-tile-title>
              </v-list-tile-content>
            </v-list-tile>
          </v-list>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            @click="assignPlaylistModalVisible = false"
            flat
          >{{ $t("general.close") }}</v-btn>
          <v-btn
            @click.prevent="assignPlaylistBoxes()"
            color="primary"
          >{{ $t("general.assign") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-speed-dial
      v-if="$store.getters.hasPermission('edit_organization_playlists')"
      fixed
      bottom
      right
      open-on-hover
    >
      <v-btn
        slot="activator"
        color="accent"
        dark
        fab
        hover
        @click="createPlaylistModalVisible = true"
      >
        <v-icon>add</v-icon>
      </v-btn>
    </v-speed-dial>
  </v-flex>
</template>

<script>
import Vue from 'vue';
import axios from 'axios';
import VeeValidate from 'vee-validate';
import VueTableMixin from '@/components/generic/VueTableMixin';
import VueTableMaterialDesignCheckbox from '@/components/generic/VueTableMaterialDesignCheckbox.vue';
import PlaylistNameEditInput from './PlaylistNameEditInput.vue';

Vue.use(VeeValidate, { fieldsBagName: 'veeErrors' });

export default {
  mixins: [
    VueTableMixin,
  ],
  data() {
    return {
      fields: [
        {
          name: VueTableMaterialDesignCheckbox,
          titleClass: 'text-xs-left',
          dataClass: 'text-xs-left',
        },
        {
          name: 'id',
          title: 'ID',
          sortField: 'id',
          titleClass: 'text-xs-left',
          dataClass: 'text-xs-left',
        },
        {
          name: PlaylistNameEditInput,
          title: this.$i18n.t('general.name'),
          titleClass: 'text-xs-left',
          dataClass: 'text-xs-left',
        },
        {
          name: 'actions-slot',
          title: this.$i18n.tc('general.action', 2),
          titleClass: 'text-xs-center',
          dataClass: 'text-xs-center',
        },
      ],
      css: {
        table: {
          tableClass: 'v-table v-datatable',
          ascendingIcon: 'mdi mdi-sort-ascending',
          descendingIcon: 'mdi mdi-sort-descending',
        },
      },
      sortOrder: [
        { field: 'id', sortField: 'id', direction: 'asc' },
      ],
      newPlaylist: {
        name: null,
      },
      createPlaylistModalVisible: false,
      deletePlaylistModalVisible: false,
      assignPlaylistModalVisible: false,
      massDeletePlaylistsModalVisible: false,
      boxes: [],
      groups: [],
      groupSelected: null,
      boxesToAssign: [],
      currentPlaylist: null,
    };
  },
  mounted() {
    const vm = this;

    axios.get(`${window.api_url}api/groups`, {

    })
      .then((response) => {
        if (response.data.length) {
          this.groups = response.data;
        }
      })
      .catch((error) => {
        vm.$toasted.show(error.response.data.message, { duration: 3000, singleton: true, type: 'error' });
      });

    axios.get(`${this.apiUrl}api/boxes`, {
      params: {
        paginate: false,
      },
    })
      .then((response) => {
        vm.boxes = response.data.data;
      })
      .catch((error) => {
        vm.$toasted.show(error.response.data.message, { duration: 3000, singleton: true, type: 'error' });
      });
  },
  computed: {
    allBoxesSelected() {
      return this.boxes.length === this.boxesToAssign.length;
    },
    someBoxesSelected() {
      return this.boxesToAssign.length > 0 && this.boxesToAssign.length < this.boxes.length;
    },
  },
  methods: {
    toggleAll(event) {
      // console.log(event);
      if (event) {
        this.boxesToAssign = this.boxes.map((box) => box.id);
      } else {
        this.boxesToAssign = [];
      }
      this.groupSelected = null;
    },
    resetGroup() {
      this.groupSelected = null;
    },
    showDeletePlaylistDialog(playlist) {
      this.currentPlaylist = playlist;
      this.deletePlaylistModalVisible = true;
    },
    showAssignPlaylistDialog(playlist) {
      this.currentPlaylist = playlist;
      this.getBoxPlaylists();
      this.groupSelected = null;
      this.assignPlaylistModalVisible = true;
    },
    createPlaylist() {
      this.createPlaylistModalVisible = false;
      this.createItem('playlist', {
        name: this.newPlaylist.name,
        width: this.newPlaylist.width,
        height: this.newPlaylist.height,
      });
      this.resetNewPlaylistData();
    },
    deletePlaylist() {
      this.deletePlaylistModalVisible = false;
      this.deleteItem('playlist', this.currentPlaylist.id);
    },
    massDeletePlaylists() {
      this.massDeletePlaylistsModalVisible = false;
      this.massDeleteItems('playlists', {
        data: {
          ids: this.$refs.vuetable.selectedTo,
        },
      });
    },
    resetNewPlaylistData() {
      this.newPlaylist.name = null;
      this.newPlaylist.width = null;
      this.newPlaylist.height = null;
    },
    getBoxPlaylists() {
      const vm = this;

      axios.get(`${this.apiUrl}api/playlist/${this.currentPlaylist.id}/boxes`)
        .then((response) => {
          vm.boxesToAssign = response.data.data;
        })
        .catch((error) => {
          vm.$toasted.show(error.response.data.message, { duration: 3000, singleton: true, type: 'error' });
        });
    },
    onChange() {
      const boxarray = [];

      const boxes = this.groupSelected;
      boxes.forEach((v) => {
          boxarray.push(v.id);
      });

      this.boxesToAssign = boxarray.concat(
        this.boxesToAssign.filter((item) => boxarray.indexOf(item) < 0),
      );
    },
    assignPlaylistBoxes() {
      const vm = this;

      vm.assignPlaylistModalVisible = false;

      axios.post(`${this.apiUrl}api/playlist/${this.currentPlaylist.id}/boxes`, {
        ids: this.boxesToAssign,
      })
        .then((response) => {
          vm.boxesToAssign = response.data.data;
          vm.$toasted.show(response.data.message, { duration: 3000, singleton: true, type: 'success' });
          vm.$refs.vuetable.refresh();
        })
        .catch((error) => {
          vm.$toasted.show(error.response.data.message, { duration: 3000, singleton: true, type: 'error' });
          vm.assignPlaylistModalVisible = false;
        });
    },
    setActivePlaylist(playlist) {
      this.$store.dispatch('SET_ACTIVE_PLAYLIST', playlist);
    },
  },
};
</script>

<style scoped>

</style>
